import JASSUB from 'jassub'
import modernWasmUrl from 'jassub/dist/wasm/jassub-worker-modern.wasm?url'
import wasmUrl from 'jassub/dist/wasm/jassub-worker.wasm?url'
import workerUrl from 'jassub/dist/worker/worker.js?worker&url'
import { toast } from 'svelte-sonner'

import { describeError } from './errors'

import type { Message } from 'chromecast-caf-receiver/cast.framework.system'

const fontExtensions = ['ttf', 'ttc', 'woff', 'woff2', 'otf', 'cff', 'otc', 'pfa', 'pfb', 'pcf', 'fnt', 'bdf', 'pfr', 'eot']
const fontRx = new RegExp(`.(${fontExtensions.join('|')})$`, 'i')

interface SubtitleTrack {
  number: string
  language: string
  type: string
  _compressed: boolean
  default: boolean
  forced: boolean
  name?: string
  header?: string
}

const AVAILABLE_FONTS = {
  'Roboto Medium': '/Roboto.woff2',
  'Gandhi Sans': '/GandhiSans-Bold.woff2',
  'Noto Sans': '/NotoSans-Bold.woff2',
  'Noto Sans JP Bold': '/NotoSansJP.woff2',
  'Noto Sans KR Bold': '/NotoSansKR.woff2',
  'Noto Sans HK': '/NotoSansHK.woff2'
}

const LANGUAGE_OVERRIDES: Record<string, string> = {
  jpn: 'Noto Sans JP Bold',
  kor: 'Noto Sans KR Bold',
  chi: 'Noto Sans HK',
  ja: 'Noto Sans JP Bold',
  ko: 'Noto Sans KR Bold',
  zh: 'Noto Sans HK'
}

const defaultHeader = `[Script Info]
Title: English (US)
ScriptType: v4.00+
WrapStyle: 0
PlayResX: 1920
PlayResY: 1080
ScaledBorderAndShadow: yes

[V4+ Styles]
Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding
Style: Default, Roboto Medium,52,&H00FFFFFF,&H00FFFFFF,&H00000000,&H00000000,0,0,0,0,100,100,0,0,1,2.6,0,2,20,20,46,1
[Events]
Format: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text

`

export class SubtitleManager {
  jassub?: JASSUB
  video = document.querySelector('cast-media-player')!.shadowRoot!.querySelector('video')!
  current: number | string = -1
  fonts: string[] = []
  subtitleLanguage = 'eng'

  _tracks: Record<number | string, { events: Map<string, { text: string, time: number, duration: number }>, meta: SubtitleTrack }> = {}

  destroy () {
    this.jassub?.destroy().catch(e => toast.error('Could not destroy subtitle renderer', { description: describeError(e) }))
    for (const { events } of Object.values(this._tracks)) {
      events.clear()
    }
    this.fonts = []
  }

  constructor () {
    // some TVs have broken requestVideoFrameCallback implementations, and only the first frame fires the callback, so check if more than 3s of video played via RAF, anmd if <2 rvfcs fired, patch the video's rvfc
    // this is VERY bad on some TVs like LG webOS, which doesnt expose ANY information about the video capabilities, like frameCounts, timmings, etc, so a proper rvfc polyfill cant be made
    let rvfcCount = 0
    const targetRFVCs = 5
    const video = this.video
    const originalRVFC = video.requestVideoFrameCallback.bind(video)
    originalRVFC(function cb () {
      rvfcCount++
      if (rvfcCount > targetRFVCs) return
      originalRVFC(cb)
    })
    let timeUpdateCount = 0
    const cb2 = () => {
      ++timeUpdateCount
      if (timeUpdateCount > 10) {
        video.removeEventListener('timeupdate', cb2)
        if (rvfcCount < targetRFVCs) {
          toast('Detected video metadata bug, applying workaround', { duration: 1000 })
          // patch rvfc
          video.requestVideoFrameCallback = function (callback: VideoFrameRequestCallback): number {
            requestAnimationFrame(now => {
              callback(now, {
                presentationTime: now,
                expectedDisplayTime: now,
                width: video.videoWidth,
                height: video.videoHeight,
                mediaTime: video.currentTime,
                presentedFrames: 0
              })
            })
            return 0
          }
          if (this.jassub) {
            const now = performance.now()
            this.jassub._handleRVFC({
              presentationTime: now,
              expectedDisplayTime: now,
              width: video.videoWidth,
              height: video.videoHeight,
              mediaTime: video.currentTime,
              presentedFrames: 0
            })
          }
        }
      }
    }
    video.addEventListener('timeupdate', cb2)
  }

  setSrc () {
    this.destroy()
    // some TVs don't support WebGL properly.... insane
    const jassub = new JASSUB({
      video: this.video,
      subContent: defaultHeader,
      workerUrl,
      modernWasmUrl,
      wasmUrl,
      queryFonts: 'localandremote',
      maxRenderHeight: 1080,
      availableFonts: AVAILABLE_FONTS
    })
    jassub.ready.catch(e => {
      if (this.jassub !== jassub) return
      toast.error('Subtitle renderer failed', { description: describeError(e) })
    })
    this.jassub = jassub
  }

  track (trackNumber: number | string) {
    const tracks = this._tracks
    if (tracks[trackNumber]) {
      return tracks[trackNumber]
    } else {
      tracks[trackNumber] = {
        events: new Map(),
        // @ts-expect-error initializing with empty object
        meta: {},
        styles: {}
      }
      return tracks[trackNumber]!
    }
  }

  async subtitle (subtitle: { text: string, time: number, duration: number, style?: string }, trackNumber: number) {
    const { events, meta } = this.track(trackNumber)
    const key = subtitle.text + subtitle.duration.toString() + subtitle.time.toString()
    if (events.has(key)) return
    const event = this.constructSub(subtitle, meta.type !== 'ass', events.size)
    events.set(key, event)
    if (Number(this.current) === trackNumber) {
      this.jassub?.renderer.processChunk(event.text, event.time, event.duration)
    }
  }

  async _ready () {
    try {
      await this.jassub?.ready
      return true
    } catch {
      return false
    }
  }

  async selectCaptions (trackNumber: number | string) {
    this.current = trackNumber

    if (!this.jassub) return

    if (!await this._ready()) return

    const target = this.jassub instanceof JASSUB ? this.jassub.renderer : this.jassub
    if (trackNumber === -1) {
      await target.setTrack(defaultHeader)
      return await this.jassub.resize()
    }

    const track = this._tracks[trackNumber]
    if (!track) return

    await target.setTrack(track.meta.header?.slice(0, -1) ?? defaultHeader)
    for (const { text, duration, time } of track.events.values()) await this.jassub.renderer.processChunk(text, time, duration)
    const lang = track.meta.language
    if (LANGUAGE_OVERRIDES[lang]) {
      const name = LANGUAGE_OVERRIDES[lang]
      await target.setDefaultFont(name)
    } else {
      await target.setDefaultFont('roboto medium')
    }
    await this.jassub.resize()
  }

  async tracks (tracklist: SubtitleTrack[]) {
    for (const track of tracklist) {
      const newtrack = this.track(track.number)
      if (track.header?.startsWith('[Script Info]')) track.type = 'ass'
      track.header ??= defaultHeader
      newtrack.meta = track
    }

    if (!await this._ready()) return

    if (!this.subtitleLanguage) return // if lang set to none dont autoselect

    const tracks = Object.entries(this._tracks)

    if (!tracks.length) return
    if (tracks.length === 1) return await this.selectCaptions(tracks[0]![0])

    const audioLanguage = 'jpn'

    const selectDesired = async (filteredTracks: typeof tracks) => {
      if (filteredTracks.length === 1) return await this.selectCaptions(filteredTracks[0]![0])

      const [desired] =
          // forced for the curent audio lang
          filteredTracks.find(([_, { meta }]) => {
            return meta.language === audioLanguage && meta.forced
          }) ??
          // non-forced for not the current audio lang
          filteredTracks.find(([_, { meta }]) => {
            return meta.language !== audioLanguage && !meta.forced
          }) ??
          // default
          filteredTracks.find(([_, { meta }]) => meta.default) ??
          filteredTracks[0]!

      return await this.selectCaptions(desired)
    }

    const wantedLanguages = tracks.filter(([_, { meta }]) => (meta.language ?? 'eng') === this.subtitleLanguage)
    if (wantedLanguages.length) {
      return await selectDesired(wantedLanguages)
    }

    const englishFallback = tracks.filter(([_, { meta }]) => (meta.language ?? 'eng') === 'eng')
    if (englishFallback.length) {
      return await selectDesired(englishFallback)
    }

    await this.selectCaptions(tracks[0]![0])
  }

  async attachments (attachments: Array<{ filename: string, mimetype: string, id: number, lan: string}>) {
    const filtered = attachments.filter(attachment => (fontRx.test(attachment.filename) || attachment.mimetype.toLowerCase().includes('font')) && !this.fonts.includes(attachment.lan))
    const urls = filtered.map(a => a.lan)
    this.fonts.push(...urls)
    // The Cast runtime blocks cross-origin requests from workers, so the jassub worker cannot fetch these URLs. Fetch them on the page.
    const results = await Promise.allSettled(urls.map(async url => {
      const res = await fetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`)
      return new Uint8Array(await res.arrayBuffer())
    }))
    const fonts: Uint8Array[] = []
    for (const result of results) {
      if (result.status === 'fulfilled') fonts.push(result.value)
      else toast.error('Could not load font', { description: describeError(result.reason) })
    }
    const jassub = this.jassub
    await jassub?.ready
    await jassub?.renderer.addFonts(fonts)
  }

  constructSub (subtitle: { text: string, time: number, duration: number}, isNotAss: boolean, subtitleIndex: number) {
    if (!isNotAss) {
      subtitle.text = subtitle.text.replace(/\r?\n/g, '')
      return subtitle
    }
    let text = subtitle.text ?? ''
    // converts VTT or other to SSA
    const matches: string[] | null = text.match(/<[^>]+>/g) // create array of all tags
    if (matches) {
      matches.forEach(match => {
        if (match.includes('</')) { // check if its a closing tag
          text = text.replace(match, match.replace('</', '{\\').replace('>', '0}'))
        } else {
          text = text.replace(match, match.replace('<', '{\\').replace('>', '1}'))
        }
      })
    }
    // replace all html special tags with normal ones
    text = text.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, '\\h').replace(/\r?\n/g, '\\N')

    // "64,0,Default,,0,0,0,,Hello!"

    return { ...subtitle, text: `${subtitleIndex},0,Default,,0,0,0,,${text}` }
  }

  cycleSubtitles () {
    const entries = Object.entries(this._tracks)
    if (!entries.length) return
    const index = entries.findIndex(([index]) => index === this.current) + 1
    const [id, info] = entries.at(index) ?? [-1, { meta: { name: 'Off', language: 'Eng' } }]
    toast(`Selected subtitle track: ${info.meta.name ?? ''} - ${info.meta.language ?? 'Eng'}`)
    this.selectCaptions(id).catch(e => toast.error('Could not switch subtitle track', { description: describeError(e) }))
    return id
  }

  async onmessage ({ data }: Message) {
    try {
      if (!await this._ready()) return
      switch (data.type) {
        case 'tracks': {
          await this.tracks(data.tracks)
          break
        }
        case 'subtitle': {
          await this.subtitle(data.subtitle, data.trackNumber)
          break
        }
        case 'attachments': {
          await this.attachments(data.attachments)
          break
        }
        case 'eval': {
          // eslint-disable-next-line no-eval
          toast(eval(data.code))
          break
        }
      }
    } catch (e) {
      toast.error('Error processing message', { description: describeError(e) })
    }
  }
}
